import React, { useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import { Button } from '@enamelai/design-system'
import './PlaidLink.css'
import {
  createLinkToken,
  exchangePublicTokenForAccessToken,
  getLinkStatus,
  testWebhook,
  unlinkClinic,
} from '@services/api/tracking.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'

type PlaidLinkProps = {
  onLinkExit?: () => void
  onLinkSuccess?: () => void
  onLinkOpen?: () => void
  onUnlink?: () => void
  onStatusChange?: (details: {
    isLinked: boolean
    linkedInstitutionName: string
  }) => void
  enableTestWebhook?: boolean
}

const PlaidLink: React.FC<PlaidLinkProps> = ({
  onLinkExit,
  onLinkSuccess,
  onLinkOpen,
  onUnlink,
  onStatusChange,
  enableTestWebhook = false,
}) => {
  const [linkToken, setLinkToken] = useState<string>('')
  const [isLinked, setIsLinked] = useState<boolean>(false)
  const [linkedInstitutionName, setLinkedInstitutionName] = useState<string>('')
  const [showConfirmUnlinkModal, setShowConfirmUnlinkModal] =
    useState<boolean>(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const onSuccess = React.useCallback(
    async (public_token: string) => {
      showLoading({
        container: '.plaid-link-settings',
      })
      const response = await exchangePublicTokenForAccessToken(public_token)

      if (response.statusCode === 200) {
        const institutionName = response.data.institution_name
        setIsLinked(true)
        setLinkedInstitutionName(institutionName)
        hideLoading('.plaid-link-settings')
        showToast(
          createSuccessToast(
            'Bank linked successfully',
            'Bank has been linked successfully.',
          ),
        )
        onLinkSuccess?.()
      } else {
        hideLoading('.plaid-link-settings')
        showToast(
          createFailedToast(
            'Bank linking failed',
            'Bank linking failed. Please try again.',
          ),
        )
      }
    },
    [hideLoading, onLinkSuccess, showLoading, showToast],
  )
  const onExit = React.useCallback(() => {
    onLinkExit?.()
  }, [onLinkExit])

  let isOauth = false
  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken!,
    onSuccess,
    onExit,
  }

  if (window.location.href.includes('?oauth_state_id=')) {
    config.receivedRedirectUri = window.location.href
    isOauth = true
  }

  const { open, ready } = usePlaidLink(config)

  useEffect(() => {
    if (isOauth && ready) {
      open()
    }
  }, [ready, open, isOauth])

  const fetchLinkToken = async (): Promise<void> => {
    const response = await createLinkToken()
    if (response.statusCode === 200) {
      setLinkToken(response.data.link_token)
    }
  }

  useEffect(() => {
    const checkLinkStatus = async (): Promise<void> => {
      const response = await getLinkStatus()
      if (response.statusCode === 200) {
        const linkStatus = response.data.link_status === 'linked'
        setIsLinked(linkStatus)
        if (!linkStatus) {
          fetchLinkToken()
        } else {
          setLinkedInstitutionName(response.data.institution_name)
        }
      }
    }

    checkLinkStatus()
  }, [])

  const handleUnlinkClinic = async (): Promise<void> => {
    const response = await unlinkClinic()
    if (response.statusCode === 200) {
      setIsLinked(false)
      setLinkedInstitutionName('')
      fetchLinkToken()
      onUnlink?.()
    }
  }

  useEffect(() => {
    onStatusChange?.({
      isLinked,
      linkedInstitutionName,
    })
  }, [isLinked, linkedInstitutionName, onStatusChange])

  return (
    <div className="plaid-link-settings">
      {!isLinked ? (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={() => {
            onLinkOpen?.()
            open()
          }}
          disabled={!ready}
        >
          Link bank
        </Button>
      ) : (
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          onClick={() => setShowConfirmUnlinkModal(true)}
        >
          Unlink
        </Button>
      )}
      {isLinked && enableTestWebhook && (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={() => testWebhook()}
        >
          Test webhook
        </Button>
      )}
      <ConfirmDeleteModal
        show={showConfirmUnlinkModal}
        onHide={() => {
          setShowConfirmUnlinkModal(false)
        }}
        onConfirm={() => {
          showLoading({
            container: '.plaid-link-settings',
          })
          handleUnlinkClinic()
            .then(() => {
              hideLoading('.plaid-link-settings')
              showToast(
                createSuccessToast(
                  'Bank unlinked successfully',
                  'Bank has been unlinked successfully.',
                ),
              )
            })
            .catch(() => {
              hideLoading('.plaid-link-settings')
              showToast(
                createFailedToast(
                  'Bank unlinking failed',
                  'Bank unlinking failed. Please try again.',
                ),
              )
            })
            .finally(() => {
              setShowConfirmUnlinkModal(false)
            })
        }}
        title="Confirm Unlink"
        message="Are you sure you want to unlink the institution"
        confirmButtonText="Unlink"
        entityName={linkedInstitutionName}
      />
    </div>
  )
}

PlaidLink.displayName = 'PlaidLink'

export default PlaidLink
